import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { submitQA } from "../redux/actions";
import useIntersectionObserver from "../hooks/useIntersectionObserver";
import styled from "styled-components";
import theme from "../theme/theme";
import { QuestionnaireTitle } from "../components/Base/QuestionnaireTitle";
import Tooltip from "../components/Base/Tooltip";
import Card from "../components/Base/Card";
import GoalIcon from "../components/GoalIcon";
import { analytics } from "../analytics";

const Wrapper = styled.div`
  width: 100%;
  max-width: 1076px;

  .questionnaireHeader {
    display: flex;
  }

  .icon {
    display: inline-block;
    width: 105px;
    height: 105px;
    background-color: white;

    svg {
      height: 100%;
      width: 100%;
    }
  }

  .header {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 30px;
    padding: 10px 0;
  }

  .goalType {
    text-align: left;
    text-transform: uppercase;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    padding: 0% 5%;
    .icon {
      display: none;
    }

    .header {
      margin-left: 0px;
    }
  }

  @media (max-width: 1075px) {
    padding: 0% 5%;
  }
`;

const CheckboxWrapper = styled.div`
  margin-top: 14%;
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;

  > * {
    flex: 0 1 0%;
  }
`;

const Checkbox = styled.div`
  position: relative;

  label {
    z-index: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  input[type="radio"] {
    z-index: 5;
    padding: 60px 80px;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    outline: 2px solid ${theme.colors.primary};
    background-color: ${theme.colors.white};
    border-radius: 4px;
    box-shadow: none;
    font-size: 2em;
  }

  input:checked,
  input:hover {
    outline-color: ${theme.colors.primaryStrong};
    background-color: ${theme.colors.primaryStrong};
    box-shadow: 0px 3px 6px #00000029;

    & + label {
      color: ${theme.colors.white};
    }
  }

  @media (max-width: 480px) {
    input[type="radio"] {
      padding: 40px 50px;
    }
    label {
      font-size: 13px;
    }
  }
`;

const InlineSingleChoice = ({ stepProps, questionDetails }) => {
  const { setNextBtnDisabled, setNextAction } = stepProps;
  const [answer, setAnswer] = useState("");
  const dispatch = useDispatch();
  const { goal } = useSelector((state) => state.user);
  const ref = useRef(null);
  const entry = useIntersectionObserver(ref, {});
  const isVisible = !!entry?.isIntersecting;

  const submitData = useCallback(async () => {
    const data = {
      goalId: goal.id,
      userAnswers: [
        {
          questionId: questionDetails.id,
          answer,
        },
      ],
    };
    const response = await dispatch(submitQA(data));

    analytics.track("Question Answered", {
      goalId: goal?.id || "",
      goalType: goal?.type || "",
      questionScreen: questionDetails?.text || "",
      questionText: questionDetails?.text || "",
      questionInput: answer || "",
    });

    return response;
  }, [dispatch, goal.id, answer, questionDetails.id]);

  useEffect(() => {
    if (isVisible) {
      setNextBtnDisabled(!answer);
      setNextAction((prevAction) => (prevAction = submitData));
    }
  }, [isVisible, answer, setNextBtnDisabled, setNextAction, submitData]);

  return (
    <Wrapper ref={ref} tabindex="-1">
      <div className="questionnaireHeader">
        <Card className="icon">
          <GoalIcon type={goal.type} />
        </Card>
        <div className="header">
          <span className="goalType">{goal.type}</span>
          <QuestionnaireTitle>
            {questionDetails.text}{" "}
            <Tooltip text={questionDetails?.tooltipText} />
          </QuestionnaireTitle>
        </div>
      </div>
      <form action="">
        <CheckboxWrapper>
          {questionDetails?.offeredAnswers.map((qDetails) => (
            <Checkbox key={qDetails.id}>
              <input
                tabindex="-1"
                id={qDetails.id}
                type="radio"
                name="riskLevel"
                aria-label={qDetails?.text}
                onChange={(e) => setAnswer(e.target.ariaLabel)}
              />
              <label htmlFor={qDetails.id}>{qDetails.text}</label>
            </Checkbox>
          ))}
        </CheckboxWrapper>
      </form>
    </Wrapper>
  );
};

export default InlineSingleChoice;
